// Theme: dark (default), light, or system (follow prefers-color-scheme).
//
// The early script in index.html sets data-theme on first paint to avoid
// FOWT. This module is the runtime API for reading + changing it.
//
// localStorage key:
//   "frozen-fly:theme" → "light" | "dark"  (absent = follow system)

export type ThemePref = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const KEY = 'frozen-fly:theme';
const MEDIA = '(prefers-color-scheme: light)';

type Listener = (theme: ResolvedTheme) => void;
const listeners = new Set<Listener>();

/** What the user picked (or "system" if no override). */
export function getThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch { /* ignore */ }
  return 'system';
}

/** What the page is actually rendering as right now. */
export function getResolvedTheme(): ResolvedTheme {
  const pref = getThemePref();
  if (pref !== 'system') return pref;
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia(MEDIA).matches ? 'light' : 'dark';
}

/** Persist user choice + apply immediately. Pass "system" to clear override. */
export function setThemePref(pref: ThemePref): void {
  try {
    if (pref === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, pref);
  } catch { /* ignore */ }
  applyResolved();
}

function applyResolved(): void {
  const resolved = getResolvedTheme();
  const dataAttr = resolved === 'light' ? 'frozen-fly-light' : 'frozen-fly';
  document.documentElement.setAttribute('data-theme', dataAttr);
  document.documentElement.style.colorScheme = resolved;
  for (const l of listeners) l(resolved);
}

/** Subscribe to theme changes (both system + user pref). Returns unsubscribe. */
export function onThemeChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Wire system-pref listener once at boot. Idempotent. */
let initialized = false;
export function initTheme(): void {
  if (initialized) return;
  initialized = true;
  if (typeof window === 'undefined') return;
  const mq = window.matchMedia(MEDIA);
  mq.addEventListener('change', () => {
    // Only re-apply if user is in "system" mode — otherwise their explicit
    // override stays in effect regardless of OS theme.
    if (getThemePref() === 'system') applyResolved();
  });
}
